import { EmptyContent, scrollable, useCurrentRelease } from "@/common";
import useSettings from "@/common/Settings";
import { FocusGraph } from "@/components/FocusGraph";
import { useGraphVisualisation } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import { TempTimeline } from "@/components/TemporalValue/TempTimeline";
import { END_TIME, START_TIME } from "@/components/TemporalValue/TempTimeline/redux";
import { isFeatureEnabled } from "@/config/features";
import { graphlib } from "@dagrejs/dagre";
//import { TempTimeline } from '@/pages/decisions/DecisionDashboard/Reports/Details/TempTimeline';
import styled from "@emotion/styled";
import { getNodeResult } from "@imminently/decisively-core";
import AttributeName from "@imminently/decisively-ui/AttributeName";
import AttributeValue from "@imminently/decisively-ui/AttributeValue";
import CheckboxField from "@imminently/decisively-ui/CheckboxField";
import GraphProvider from "@imminently/decisively-ui/GraphProvider";
import Icon from "@imminently/decisively-ui/Icon";
import NodePath from "@imminently/decisively-ui/NodePath";
import Tabs from "@imminently/decisively-ui/Tabs";
import TemporalValue from "@imminently/decisively-ui/TemporalValue";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import { Card, CardContent } from "@mui/material";
import { Alert } from "@mui/material";
import Typography from "@mui/material/Typography";
import { clsx } from "clsx";
import { get, groupBy, sortBy } from "lodash";
import { useState } from "react";
import NodeInputs from "./NodeInputs";
import { useResolveErrorInfo } from "./ResolveErrorInfo";

const TEMP_TIMELINE_WRAP_CLS = "tempTimelineWrap";

const LineIndex = styled(Typography)`
  border-radius: 50%;
  background-color: var(--foreground);
  color: #fff;
  width: 1.125rem;
  height: 1.125rem;
  text-align: center;
  line-height: 1.125rem;
  flex-shrink: 0;
`;
const StyledFocusGraph = styled(FocusGraph)`
  height: 100%;
`;
const LineValuesWrap = styled.div`
  margin-top: 0.75rem;

  h3 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  ol {
    font-size: 0.75rem;
    font-weight: 400;
  }

  ol li {
    cursor: pointer;
    padding: 0.25rem;

    &:hover {
      background-color: var(--secondary-hover);
      border-radius: 0.25rem;
    }

    .MuiTypography-root {
      text-decoration: underline;
    }

    >div {
      display: flex;
      justify-content: space-between;
      align-items: center;
      > *:last-child {
        max-width: 50%;
        white-space: normal;
      }
    }
  }
`;

const ValueDisplayContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    background-color: var(--secondary-hover);
    border-radius: 0.25rem;
  }

  .MuiTypography-root {
    text-decoration: underline;
  }

  .value-container {
    text-align: right;
    max-width: 50%;
    white-space: normal;
  }
`;

const ValueDisplay = ({ node, graph, setSelectedNodeId, showNumber = true, index, useListItem = true }) => {
  const showReason = isFeatureEnabled("experimental.extended_debug_diagnostics");
  const content = (
    <ValueDisplayContainer
      onClick={() => {
        setSelectedNodeId(node.path || node.id);
      }}
    >
      <div className={"flex gap-2"}>
        {showNumber && (
          <LineIndex
            variant="caption"
            className={"no-underline!"}
          >
            {index + 1}
          </LineIndex>
        )}
        <AttributeName node={node} />
      </div>

      <div className="value-container">
        <AttributeValue
          node={node}
          showType={false}
          hideTemporal={true}
          showReason={showReason}
        />
      </div>
    </ValueDisplayContainer>
  );

  return useListItem ? <li>{content}</li> : content;
};

const TimelineDisplay = ({ node, showNumber = true, index, inMergedMode = false }) => {
  let value = getNodeResult(node);
  if (!get(value, "temporal")) {
    if (inMergedMode) return null;
    value = {
      temporal: {
        ranges: [
          {
            r: {
              start: START_TIME,
              end: END_TIME,
            },
            v: value,
          },
        ],
      },
    };
  }

  return (
    <div className={`flex items-center gap-4 ${TEMP_TIMELINE_WRAP_CLS}`}>
      {showNumber && <LineIndex variant="caption">{index + 1}</LineIndex>}
      <TemporalValue
        node={node}
        value={value}
      />
    </div>
  );
};

const Timeline = ({ inputs, graph }) => {
  const { setSelectedNodeId } = useGraphVisualisation();
  const { getSetting, setSetting } = useSettings();
  const [isMerged, setIsMerged] = useState(() => getSetting("decisionReport.timelineMerged", false));

  const handleMergedChange = (newValue) => {
    setIsMerged(newValue);
    setSetting("decisionReport.timelineMerged", newValue);
  };

  const byEntity = groupBy(inputs, (g) => {
    return g.parent_path || g.entity;
  });
  const sorted: any[] = [];
  Object.keys(byEntity).forEach((entity) => {
    const sortedByIndex = sortBy(byEntity[entity], "index");
    sorted.push(...sortedByIndex);
  });

  return (
    <>
      <div
        className={`${scrollable} flex h-full flex-col gap-1`}
        style={{ overflow: "auto" }}
      >
        <div className="mb-2 flex justify-end">
          <CheckboxField
            name="merged"
            label="Merged"
            value={isMerged}
            onValueChange={handleMergedChange}
          />
        </div>
        {isMerged ? (
          // Merged mode display
          sorted.map((node, i) => {
            const prevNode = i > 0 ? sorted[i - 1] : null;
            const entityChanged = !prevNode || prevNode.entity !== node.entity;
            const indexChanged = !prevNode || prevNode.index !== node.index;

            return (
              <div key={i}>
                {(entityChanged || indexChanged) && (
                  <Typography
                    variant="h6"
                    className="pt-2"
                  >
                    <NodePath
                      node={node}
                      showDescription={false}
                    />
                  </Typography>
                )}
                <div className="pb-2">
                  <ValueDisplay
                    node={node}
                    graph={graph}
                    setSelectedNodeId={setSelectedNodeId}
                    showNumber={false}
                    index={i}
                    useListItem={false}
                  />
                </div>
                <TimelineDisplay
                  inMergedMode={true}
                  node={node}
                  showNumber={false}
                  index={i}
                />
              </div>
            );
          })
        ) : (
          // Original mode display
          <>
            {sorted.map((node, i) => (
              <TimelineDisplay
                key={i}
                node={node}
                index={i}
              />
            ))}
            <LineValuesWrap>
              <Typography variant="h6">Values</Typography>
              <ol>
                {sorted.map((node, i) => {
                  const prevNode = i > 0 ? sorted[i - 1] : null;
                  const entityChanged = !prevNode || prevNode.entity !== node.entity;
                  const indexChanged = !prevNode || prevNode.index !== node.index;

                  return (
                    <div key={i}>
                      {(entityChanged || indexChanged) && (
                        <Typography
                          variant="h6"
                          className="pt-2"
                        >
                          <NodePath
                            node={node}
                            showDescription={false}
                          />
                        </Typography>
                      )}
                      <ValueDisplay
                        node={node}
                        graph={graph}
                        setSelectedNodeId={setSelectedNodeId}
                        index={i}
                        showNumber={true}
                        useListItem={true}
                      />
                    </div>
                  );
                })}
              </ol>
            </LineValuesWrap>
          </>
        )}
      </div>
    </>
  );
};

const StyledCardContent = styled(CardContent)`
  height: 100%;

  #simple-tabpanel-2 {
    height: calc(100vh - 245px);

    >div { height: 100%; }
  }
`;

export const AttributeValueTabs = () => {
  const { graph, selectedNode } = useGraphVisualisation();

  // Get the inputs to this node
  const items = ((selectedNode && graph.successors(selectedNode.path || selectedNode.id)) || [])
    .map((child) => {
      const node = graph.node(child);
      if (node.hidden) return;
      return node;
    })
    .filter((x) => x);

  const errorReason = selectedNode?.debugInfo?.reason;
  const error = useResolveErrorInfo({
    error: errorReason,
    node: selectedNode,
  });
  if (!selectedNode) return null;

  // Check if there are any relationships we use, but are blank (which could be an error)
  if (items.length === 0) {
    if (error) {
      return (
        <>
          <Alert
            severity="error"
            className="mb-4"
          >
            {error}
          </Alert>
          <EmptyContent
            className="h-full"
            messages={["No input values to display"]}
          />
        </>
      );
    }
    return (
      <EmptyContent
        className="h-full"
        messages={["No input values to display"]}
      />
    );
  }

  console.log("selectedNode", selectedNode);

  return (
    <GraphProvider graph={graph}>
      {error && (
        <Alert
          className="mb-4"
          severity="error"
        >
          {error}
        </Alert>
      )}
      <Tabs
        defaultValue="timeline"
        className="flex flex-col h-full"
      >
        <Tabs.List>
          <Tabs.Trigger value="timeline">Timeline</Tabs.Trigger>
          <Tabs.Trigger value="list">List</Tabs.Trigger>
          <Tabs.Trigger value="graph">Graph</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content
          value="timeline"
          className={clsx(scrollable, "p-2 h-full")}
        >
          <Timeline
            inputs={items}
            graph={graph}
          />
        </Tabs.Content>
        <Tabs.Content
          value="list"
          className={clsx(scrollable, "p-2")}
        >
          <NodeInputs
            node={selectedNode}
            showIntro={false}
          />
        </Tabs.Content>
        <Tabs.Content
          value="graph"
          className={clsx(scrollable, "p-2")}
        >
          <StyledFocusGraph
            graph={graphlib.json.write(graph)}
            focusNode={selectedNode}
            onRawNodeClick={() => {}}
          />
        </Tabs.Content>
      </Tabs>
    </GraphProvider>
  );
};

const AttributeValues = () => {
  return (
    <Card className="h-full overflow-auto">
      <StyledCardContent className={scrollable}>
        <AttributeValueTabs />
      </StyledCardContent>
    </Card>
  );
};

export default AttributeValues;
